import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import PageFooter from '@/components/feature/PageFooter';
import PaymentSelectModal from '@/components/feature/PaymentSelectModal';

const PRODUCT = {
  id: 3,
  name: '스피루리나 200mgX3,600정 1BOX',
  price: 250000,
  originalPrice: 320000,
  discount: 22,
  usdtPrice: 160,
  image: 'https://storage.readdy-site.link/project_files/fab39877-057d-4165-9475-a35374cb4014/39229809-5b7c-4182-a3dc-611e808ad51d___1_1.jpg?v=22f7d1d2f402b06ac30da9e380ca1fa7',
  category: 'spirulina',
};

const DETAIL_IMAGES = [
  'https://storage.readdy-site.link/project_files/fab39877-057d-4165-9475-a35374cb4014/f8496bb9-f2bd-44e5-a261-365e9c6d1347__01-1.jpg?v=65701c1ac2ec24019918af1b900cbf3c',
  'https://storage.readdy-site.link/project_files/fab39877-057d-4165-9475-a35374cb4014/4550804f-2071-4a23-a412-8f6e21be5577__02-1.jpg?v=ca3b43875ee0071b9ee9f1d9e9e930d1',
  'https://storage.readdy-site.link/project_files/fab39877-057d-4165-9475-a35374cb4014/b637f2b6-0645-4cb3-a16b-28f4bbd8c66b__03-1.jpg?v=ca895994b66a1007e0e51cb9263c6963',
];

export default function SpirilinaPage() {
  const navigate = useNavigate();
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [selectedImg, setSelectedImg] = useState(0);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const formatPrice = (p: number) => p.toLocaleString('ko-KR');

  const sections = [
    { label: '제품 소개', id: 'intro' },
    { label: '성분 & 효능', id: 'ingredients' },
    { label: '섭취 방법', id: 'howto' },
    { label: '인증 & 품질', id: 'cert' },
    { label: '리뷰', id: 'reviews' },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = sectionRefs.current.findIndex((r) => r === entry.target);
            if (idx !== -1) setActiveSection(idx);
          }
        });
      },
      { threshold: 0.3 }
    );
    sectionRefs.current.forEach((r) => r && observer.observe(r));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Noto Sans KR, sans-serif' }}>
      <Navbar />

      {/* 히어로 */}
      <section className="pt-20 pb-0 bg-gradient-to-b from-teal-50 to-white">
        <div className="max-w-6xl mx-auto px-5 md:px-8 pt-10 pb-0">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-teal-600 transition-colors mb-6 cursor-pointer"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            뒤로가기
          </button>

          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
            {/* 이미지 갤러리 */}
            <div className="w-full lg:w-[480px] flex-shrink-0">
              <div className="w-full h-[340px] md:h-[480px] rounded-2xl overflow-hidden bg-stone-50">
                <img
                  src={DETAIL_IMAGES[selectedImg]}
                  alt={`스피루리나 상세 이미지 ${selectedImg + 1}`}
                  className="w-full h-full object-cover object-top transition-all duration-300"
                />
              </div>
              {/* 썸네일 */}
              <div className="flex gap-3 mt-4">
                {DETAIL_IMAGES.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImg(i)}
                    className={`flex-1 h-20 rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                      selectedImg === i ? 'border-teal-500' : 'border-gray-100 hover:border-teal-300'
                    }`}
                  >
                    <img src={img} alt={`썸네일 ${i + 1}`} className="w-full h-full object-cover object-top" />
                  </button>
                ))}
              </div>
            </div>

            {/* 제품 정보 */}
            <div className="flex-1 pb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-teal-100 text-teal-700 text-xs font-semibold mb-3">
                대용량 · 토카이 스피루리나
              </span>
              <h1
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-2"
                style={{ fontFamily: 'Noto Serif KR, serif' }}
              >
                {PRODUCT.name}
              </h1>
              <p className="text-sm text-gray-500 mb-5">
                천연 슈퍼푸드 · 무방부제 · 무색소 · 비건 인증
              </p>

              {/* 별점 */}
              <div className="flex items-center gap-1.5 mb-5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className={`ri-star-fill text-sm w-4 h-4 flex items-center justify-center ${i < 5 ? 'text-amber-400' : 'text-gray-200'}`}></i>
                ))}
                <span className="text-sm text-gray-500 ml-1">4.8 (312개 리뷰)</span>
              </div>

              {/* 가격 */}
              <div className="bg-teal-50 rounded-xl p-4 mb-5">
                <div className="flex items-end gap-3 mb-1">
                  <span className="text-3xl font-bold text-teal-700">{formatPrice(PRODUCT.price)}원</span>
                  <span className="text-base text-gray-400 line-through mb-0.5">{formatPrice(PRODUCT.originalPrice)}원</span>
                  <span className="text-sm font-bold text-rose-500 mb-0.5">-{PRODUCT.discount}%</span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-teal-100 border border-teal-200 text-teal-700 text-xs font-semibold">
                    <i className="ri-bit-coin-line w-3 h-3 flex items-center justify-center"></i>
                    USDT ${PRODUCT.usdtPrice} (ERC-20)
                  </span>
                </div>
              </div>

              {/* 핵심 특징 */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                {[
                  { icon: 'ri-leaf-line', text: '천연 슈퍼푸드 스피루리나' },
                  { icon: 'ri-shield-check-line', text: 'HACCP 인증 시설' },
                  { icon: 'ri-flask-line', text: '무방부제·무색소' },
                  { icon: 'ri-heart-pulse-line', text: '단백질 60~70% 함유' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="w-7 h-7 flex items-center justify-center rounded-full bg-teal-100 text-teal-600 flex-shrink-0">
                      <i className={`${item.icon} text-sm`}></i>
                    </span>
                    {item.text}
                  </div>
                ))}
              </div>

              <button
                onClick={() => setPaymentOpen(true)}
                className="w-full py-4 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-bold text-base transition-colors cursor-pointer whitespace-nowrap"
              >
                지금 구매하기
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 탭 네비게이션 */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="flex gap-0 overflow-x-auto">
            {sections.map((s, i) => (
              <button
                key={s.id}
                onClick={() => sectionRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className={`px-4 md:px-6 py-4 text-sm font-semibold border-b-2 transition-all cursor-pointer whitespace-nowrap ${
                  activeSection === i
                    ? 'border-teal-600 text-teal-700'
                    : 'border-transparent text-gray-400 hover:text-gray-700'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 md:px-8 py-12 space-y-20">

        {/* 1. 제품 소개 - 첨부 이미지 전체 표시 */}
        <div ref={(el) => { sectionRefs.current[0] = el; }} id="intro">
          <SectionTitle label="제품 소개" title="토카이 스피루리나란?" color="teal" />

          {/* 첨부 이미지 3장 풀 표시 */}
          <div className="mt-8 space-y-6">
            {DETAIL_IMAGES.map((img, i) => (
              <div key={i} className="w-full rounded-2xl overflow-hidden">
                <img
                  src={img}
                  alt={`스피루리나 상세 설명 ${i + 1}`}
                  className="w-full h-auto object-contain"
                />
              </div>
            ))}
          </div>

          {/* 핵심 포인트 카드 */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              {
                icon: 'ri-seedling-line',
                title: '슈퍼푸드 스피루리나',
                desc: 'NASA가 인정한 미래 식품. 단백질 60~70%, 필수 아미노산, 비타민, 미네랄이 풍부한 천연 슈퍼푸드',
              },
              {
                icon: 'ri-drop-line',
                title: '고순도 원료',
                desc: '오염되지 않은 청정 환경에서 재배된 고순도 스피루리나만을 엄선하여 사용',
              },
              {
                icon: 'ri-award-line',
                title: '대용량 경제적 구성',
                desc: '3,600정 1BOX 대용량 구성으로 온 가족이 장기간 꾸준히 섭취 가능한 경제적인 선택',
              },
            ].map((c) => (
              <div key={c.title} className="bg-white border border-gray-100 rounded-2xl p-6">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-teal-100 text-teal-600 mb-4">
                  <i className={`${c.icon} text-lg`}></i>
                </span>
                <h4 className="font-bold text-gray-900 text-sm mb-2">{c.title}</h4>
                <p className="text-xs text-gray-500 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 2. 성분 & 효능 */}
        <div ref={(el) => { sectionRefs.current[1] = el; }} id="ingredients">
          <SectionTitle label="성분 & 효능" title="스피루리나 주요 성분과 기대 효과" color="teal" />

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                name: '식물성 단백질 (60~70%)',
                color: 'teal',
                icon: 'ri-body-scan-line',
                effects: ['근육 유지 및 합성', '포만감 지속', '체중 관리 도움', '소화 부담 적음'],
                desc: '스테이크보다 높은 단백질 함량, 필수 아미노산 18종 모두 함유',
              },
              {
                name: '피코시아닌 (Phycocyanin)',
                color: 'cyan',
                icon: 'ri-shield-line',
                effects: ['강력한 항산화 효과', '면역력 강화', '항염 작용', '뇌 건강 보호'],
                desc: '스피루리나 특유의 청록색 색소 성분, 강력한 항산화·항염 효과',
              },
              {
                name: '베타카로틴 (β-Carotene)',
                color: 'amber',
                icon: 'ri-eye-line',
                effects: ['눈 건강 보호', '피부 미용', '항산화 작용', '면역력 향상'],
                desc: '당근보다 10배 이상 풍부한 베타카로틴, 체내에서 비타민A로 전환',
              },
              {
                name: '철분 & 비타민B12',
                color: 'rose',
                icon: 'ri-heart-pulse-line',
                effects: ['빈혈 예방', '에너지 대사 촉진', '신경계 건강', '피로 회복'],
                desc: '채식주의자에게 부족하기 쉬운 철분과 비타민B12를 동시에 보충',
              },
            ].map((item) => (
              <div key={item.name} className="border border-gray-100 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className={`w-10 h-10 flex items-center justify-center rounded-full bg-${item.color}-100 text-${item.color}-600`}>
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm">{item.name}</h4>
                    <p className="text-xs text-gray-400 mt-0.5">{item.desc}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {item.effects.map((e) => (
                    <span key={e} className={`px-2.5 py-1 rounded-full bg-${item.color}-50 text-${item.color}-700 text-xs font-medium`}>
                      {e}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* 영양 성분표 */}
          <div className="mt-8 bg-stone-50 rounded-2xl p-6">
            <h4 className="font-bold text-gray-900 mb-4 text-sm">영양 성분표 (1정 200mg 기준)</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 text-gray-500 font-medium">성분</th>
                    <th className="text-right py-2 text-gray-500 font-medium">함량</th>
                    <th className="text-right py-2 text-gray-500 font-medium">%영양소 기준치</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { name: '열량', value: '0.7kcal', pct: '-' },
                    { name: '단백질', value: '120~140mg', pct: '-' },
                    { name: '지방', value: '6mg', pct: '-' },
                    { name: '탄수화물', value: '14mg', pct: '-' },
                    { name: '철분', value: '0.5mg', pct: '4%' },
                    { name: '베타카로틴', value: '0.1mg 이상', pct: '-' },
                    { name: '피코시아닌', value: '20mg 이상', pct: '-' },
                  ].map((row, i) => (
                    <tr key={row.name} className={`border-b border-gray-100 ${i % 2 === 0 ? '' : 'bg-white'}`}>
                      <td className="py-2.5 text-gray-700">{row.name}</td>
                      <td className="py-2.5 text-right text-gray-700 font-medium">{row.value}</td>
                      <td className="py-2.5 text-right text-teal-600 font-semibold">{row.pct}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* 3. 섭취 방법 */}
        <div ref={(el) => { sectionRefs.current[2] = el; }} id="howto">
          <SectionTitle label="섭취 방법" title="올바른 섭취 가이드" color="teal" />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                step: '01',
                icon: 'ri-time-line',
                title: '섭취 시기',
                desc: '식사 30분 전 또는 식사와 함께 섭취하세요. 공복 섭취 시 흡수율이 더욱 높아집니다.',
              },
              {
                step: '02',
                icon: 'ri-cup-line',
                title: '섭취 방법',
                desc: '1일 10~20정을 충분한 물(200ml 이상)과 함께 드세요. 처음에는 5정부터 시작해 점차 늘려가세요.',
              },
              {
                step: '03',
                icon: 'ri-calendar-check-line',
                title: '섭취 기간',
                desc: '최소 3개월 이상 꾸준히 섭취하시면 스피루리나의 효과를 충분히 경험하실 수 있습니다.',
              },
            ].map((item) => (
              <div key={item.step} className="bg-white border border-gray-100 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl font-black text-teal-100">{item.step}</span>
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-teal-600 text-white">
                    <i className={`${item.icon} text-base`}></i>
                  </span>
                </div>
                <h4 className="font-bold text-gray-900 text-sm mb-2">{item.title}</h4>
                <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* 주의사항 */}
          <div className="mt-6 bg-amber-50 border border-amber-100 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-amber-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0"></i>
              <div>
                <p className="text-sm font-bold text-amber-800 mb-2">섭취 시 주의사항</p>
                <ul className="space-y-1">
                  {[
                    '페닐케톤뇨증 환자는 섭취를 삼가세요.',
                    '항응고제 복용 중이신 분은 섭취 전 전문의와 상담하세요.',
                    '임산부·수유부·어린이는 섭취 전 전문의와 상담하세요.',
                    '의약품이 아니므로 질병 치료 목적으로 사용하지 마세요.',
                    '서늘하고 건조한 곳에 보관하세요.',
                  ].map((t) => (
                    <li key={t} className="text-xs text-amber-700 flex items-start gap-1.5">
                      <span className="mt-1 flex-shrink-0">·</span>{t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* 4. 인증 & 품질 */}
        <div ref={(el) => { sectionRefs.current[3] = el; }} id="cert">
          <SectionTitle label="인증 & 품질" title="믿을 수 있는 품질 보증" color="teal" />
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: 'ri-shield-check-line', title: 'HACCP 인증', desc: '식품안전관리인증기준 적합 시설' },
              { icon: 'ri-leaf-line', title: '비건 인증', desc: '동물성 원료 무첨가 비건 제품' },
              { icon: 'ri-microscope-line', title: '성분 분석', desc: '공인 기관 영양 성분 검사 완료' },
              { icon: 'ri-global-line', title: '해외 수출', desc: '미국·유럽·아시아 수출 인증 완료' },
            ].map((c) => (
              <div key={c.title} className="flex flex-col items-center text-center bg-white border border-gray-100 rounded-2xl p-5">
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-teal-100 text-teal-600 mb-3">
                  <i className={`${c.icon} text-xl`}></i>
                </span>
                <h4 className="font-bold text-gray-900 text-sm mb-1">{c.title}</h4>
                <p className="text-xs text-gray-400 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>

          {/* 제조 과정 */}
          <div className="mt-8">
            <h4 className="font-bold text-gray-900 mb-5 text-sm">제조 과정</h4>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { step: '원료 재배', desc: '청정 환경 스피루리나 재배' },
                { step: '수확 & 세척', desc: '최적 시기 수확 및 정제수 세척' },
                { step: '건조 처리', desc: '저온 건조로 영양 성분 보존' },
                { step: '분쇄 & 정제', desc: '고순도 분말 정제 과정' },
                { step: '타정 & 포장', desc: 'HACCP 시설 타정 및 포장' },
              ].map((item, i) => (
                <div key={item.step} className="bg-teal-50 rounded-xl p-4 text-center">
                  <div className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center text-xs font-bold mx-auto mb-3">
                    {i + 1}
                  </div>
                  <p className="text-sm font-bold text-gray-900 mb-1">{item.step}</p>
                  <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 5. 리뷰 */}
        <div ref={(el) => { sectionRefs.current[4] = el; }} id="reviews">
          <SectionTitle label="리뷰" title="실제 고객 후기" color="teal" />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              { name: '김미영', age: '50대 여성', text: '매일 아침 스무디에 넣어 먹고 있어요. 3개월째 꾸준히 먹고 있는데 확실히 피로감이 줄었고 피부도 좋아졌어요. 가족 모두 함께 먹고 있습니다!', rating: 5 },
              { name: '이수진', age: '30대 여성', text: '직장 생활하면서 건강 챙기기 어려웠는데, 간편하게 먹을 수 있어서 너무 좋아요. 면역력이 좋아진 느낌이에요. 강력 추천합니다!', rating: 5 },
              { name: '최영수', age: '40대 남성', text: '운동 후 단백질 보충용으로 먹고 있습니다. 천연 단백질이라 부담 없고, 회복도 빨라진 것 같아요. 대용량이라 경제적이에요.', rating: 4 },
              { name: '박준호', age: '60대 남성', text: '의사 선생님 추천으로 시작했는데 정말 효과가 있네요. 혈당 관리에도 도움이 된다고 해서 꾸준히 먹고 있습니다. 재구매 예정이에요.', rating: 5 },
            ].map((r) => (
              <div key={r.name} className="bg-white border border-gray-100 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {r.name[0]}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">{r.name}</p>
                      <p className="text-xs text-gray-400">{r.age}</p>
                    </div>
                  </div>
                  <div className="flex gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className={`ri-star-fill text-xs w-3 h-3 flex items-center justify-center ${i < r.rating ? 'text-amber-400' : 'text-gray-200'}`}></i>
                    ))}
                  </div>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{r.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 하단 CTA */}
        <div className="bg-teal-600 rounded-2xl p-8 md:p-12 text-center">
          <h3 className="text-xl md:text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Noto Serif KR, serif' }}>
            지금 바로 토카이 스피루리나를 경험해보세요
          </h3>
          <p className="text-teal-100 text-sm mb-6">천연 슈퍼푸드 · 3,600정 대용량 · 당일 포장 배송</p>
          <button
            onClick={() => setPaymentOpen(true)}
            className="px-10 py-4 rounded-xl bg-white text-teal-700 font-bold text-sm hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            구매하기 · 250,000원
          </button>
        </div>

      </div>

      <PageFooter />

      {paymentOpen && (
        <PaymentSelectModal
          product={{
            id: String(PRODUCT.id),
            name: PRODUCT.name,
            price: PRODUCT.price,
            originalPrice: PRODUCT.originalPrice,
            image: PRODUCT.image,
            category: PRODUCT.category,
            usdtPrice: PRODUCT.usdtPrice,
          }}
          onClose={() => setPaymentOpen(false)}
        />
      )}
    </div>
  );
}

function SectionTitle({ label, title, color }: { label: string; title: string; color: string }) {
  return (
    <div>
      <span className={`inline-block px-3 py-1 rounded-full bg-${color}-50 text-${color}-600 text-xs font-semibold mb-3`}>
        {label}
      </span>
      <h2 className="text-xl md:text-2xl font-bold text-gray-900" style={{ fontFamily: 'Noto Serif KR, serif' }}>
        {title}
      </h2>
    </div>
  );
}
